import { loadCSS } from "./utils";
import * as markerListeners from "./markerListeners";
import * as virtualTourListeners from "./virtualTourListeners";
import * as planListeners from "./planListeners";
import * as appendBaseUrl from "./appendBaseUrl";
import "./virtual-tour-viewer.scss";
import { intro } from "./intro";
export class VTV {
    _config;
    _initialViewerConfig = {};
    _viewer;
    _ready;
    _nodes;
    _baseUrl;
    _container;
    _configUrl;
    constructor(options) {
        let { baseUrl, configUrl, container, startNodeId, defaultYaw, defaultPitch } = options;
        __webpack_public_path__ = "/resources/";
        if (baseUrl.charAt(baseUrl.length - 1) !== "/") {
            baseUrl = baseUrl + "/";
        }
        this._baseUrl = baseUrl;
        this._container = container;
        this._configUrl = configUrl;
        this._ready = this.init({ startNodeId, defaultYaw, defaultPitch }, false);
    }
    get viewer() {
        return this._ready.then(() => this._viewer);
    }
    get config() {
        return this._ready.then(() => this._config);
    }
    init(options, setReady = true) {
        const promise = new Promise(async (resolve, reject) => {
            try {
                await this._loadConfig(options);
                await this._initViewer(options.startNodeId === undefined);
                setTimeout(() => resolve());
            }
            catch (e) {
                reject(e);
            }
        });
        if (setReady)
            this._ready = promise;
        return promise;
    }
    async _loadConfig(options) {
        if (this._config)
            console.warn("Config already loaded. Overwriting.");
        const res = await fetch(this._configUrl);
        if (!res.ok)
            throw new Error(`Failed to load config: ${res.statusText} (${res.status}): ${res.text()}`);
        this._config = await res.json();
        this._initialViewerConfig = {
            ...this._config.viewerConfig,
        };
        if (this._container)
            this._config.viewerConfig.container = this._container;
        // load plugins and adapter
        const [adapter, plugins] = await this._getModules();
        this._config.viewerConfig.adapter = adapter;
        this._config.viewerConfig.plugins = plugins;
        const virtualTour = this._config.plugins.find(p => p[0] === "virtual-tour");
        if (virtualTour) {
            this._nodes = virtualTour[1].nodes;
            this._nodes = this._nodes?.map(node => {
                node.markers = appendBaseUrl.markers(this._baseUrl, node.markers);
                return node;
            });
            if (options.startNodeId) {
                virtualTour[1].startNodeId = options.startNodeId;
            }
        }
        // set defaultYaw and defaultPitch if specified
        if (options.defaultYaw)
            this._config.viewerConfig.defaultYaw = options.defaultYaw;
        if (options.defaultPitch)
            this._config.viewerConfig.defaultPitch = options.defaultPitch;
        if (this._config.adapter[0] === "equirectangular-tiles")
            this._addTilesDetails();
        if (this._config.adapter[0] === "equirectangular-video")
            this._addVideoPanoramaDetails();
        if (this._config.introAnimation && !options.startNodeId) {
            this._config.viewerConfig.defaultZoomLvl = 0;
            this._config.viewerConfig.maxFov = 130;
            this._config.viewerConfig.fisheye = 2;
            this._config.viewerConfig.defaultPitch = -Math.PI / 2;
            this._config.viewerConfig.defaultYaw = Math.PI / 2;
        }
    }
    async _getModules() {
        if (!this._config)
            throw new Error("Config not loaded. Can't load modules.");
        const useCDN = this._config.useCDN ?? false;
        // if cdn is used, an js importmap needs to be included inside the html
        // TODO: css via custom cdn
        const plugins = [];
        let adapter;
        // load plugins
        await Promise.all(this._config.plugins.map(async (plugin) => {
            let jsImport;
            switch (plugin[0]) {
                // load all JS modules and CSS modules if needed
                case "compass":
                case "gallery":
                case "map":
                case "markers":
                case "plan":
                case "settings":
                case "video":
                case "virtual-tour":
                    await loadCSS(useCDN ?
                        `https://cdn.jsdelivr.net/npm/@photo-sphere-viewer/${plugin[0]}-plugin/index.min.css` :
                        `${this._baseUrl}resources/node_modules_css/${plugin[0]}-plugin/index.css`);
                case "autorotate":
                case "gyroscope":
                case "overlays":
                case "resolution":
                case "stereo":
                case "visible-range":
                    jsImport = useCDN ?
                        (await import(/* webpackIgnore: true */ `@photo-sphere-viewer/${plugin[0]}-plugin`)) :
                        (await import(`@photo-sphere-viewer/${plugin[0]}-plugin`));
                    break;
                case "lensflare":
                    jsImport = useCDN ?
                        (await import(/* webpackIgnore: true */ `photo-sphere-viewer-lensflare-plugin`)) :
                        (await import(`photo-sphere-viewer-lensflare-plugin`));
                    break;
                default:
                    console.warn(`Requested module "${plugin}" not available for import.`);
                    return;
            }
            switch (plugin[0]) {
                case "compass":
                    jsImport = jsImport.CompassPlugin;
                    break;
                case "gallery":
                    jsImport = jsImport.GalleryPlugin;
                    break;
                case "map":
                    jsImport = jsImport.MapPlugin;
                    break;
                case "markers":
                    jsImport = jsImport.MarkersPlugin;
                    break;
                case "plan":
                    await loadCSS(useCDN ?
                        `https://cdn.jsdelivr.net/npm/leaflet@1/dist/leaflet.css` :
                        `${this._baseUrl}resources/node_modules_css/leaflet.css`);
                    useCDN ?
                        (await import(/* webpackIgnore: true */ `leaflet`)) :
                        await import("leaflet");
                    jsImport = jsImport.PlanPlugin;
                    break;
                case "settings":
                    jsImport = jsImport.SettingsPlugin;
                    break;
                case "video":
                    jsImport = jsImport.VideoPlugin;
                    break;
                case "virtual-tour":
                    jsImport = jsImport.VirtualTourPlugin;
                    plugin[1].getLinkTooltip = (content, link, node) => {
                        if (!link.data?.tooltip)
                            return content;
                        let newContent = `<h3>${link.data.tooltip}</h3>`;
                        if (node.thumbnail) {
                            newContent += `<img src="${node.thumbnail}">`;
                        }
                        if (node.caption) {
                            newContent += `<p>${node.caption}</p>`;
                        }
                        return newContent;
                    };
                    break;
                case "autorotate":
                    jsImport = jsImport.AutorotatePlugin;
                    break;
                case "gyroscope":
                    jsImport = jsImport.GyroscopePlugin;
                    break;
                case "overlays":
                    jsImport = jsImport.OverlaysPlugin;
                    break;
                case "resolution":
                    jsImport = jsImport.ResolutionPlugin;
                    break;
                case "stereo":
                    jsImport = jsImport.StereoPlugin;
                    break;
                case "visible-range":
                    jsImport = jsImport.VisibleRangePlugin;
                    break;
                case "lensflare":
                    jsImport = jsImport.LensflarePlugin;
                    break;
            }
            plugins.push([jsImport, plugin[1]]);
        }));
        // load adapter
        let jsImport;
        switch (this._config.adapter[0]) {
            case "equirectangular":
                jsImport = useCDN ?
                    (await import(/* webpackIgnore: true */ "@photo-sphere-viewer/core")) :
                    (await import("@photo-sphere-viewer/core"));
                break;
            case "equirectangular-tiles":
                jsImport = useCDN ?
                    (await import(/* webpackIgnore: true */ `@photo-sphere-viewer/equirectangular-tiles-adapter`)) :
                    (await import(`@photo-sphere-viewer/equirectangular-tiles-adapter`));
                break;
            case "equirectangular-video":
                // case "cubemap": // TODO
                // case "cubemap-tiles": // TODO
                // case "cubemap-video": // TODO
                jsImport = useCDN ?
                    (await import(/* webpackIgnore: true */ `@photo-sphere-viewer/equirectangular-video-adapter`)) :
                    (await import(`@photo-sphere-viewer/equirectangular-video-adapter`));
                break;
            default:
                throw new Error("Invalid adapter. Cannot proceed.");
        }
        switch (this._config.adapter[0]) {
            case "equirectangular":
                adapter = [jsImport.EquirectangularAdapter, this._config.adapter[1]];
                break;
            case "equirectangular-tiles":
                adapter = [jsImport.EquirectangularTilesAdapter, this._config.adapter[1]];
                break;
            case "equirectangular-video":
                adapter = [jsImport.EquirectangularVideoAdapter, this._config.adapter[1]];
                break;
            // case "cubemap": // TODO
            //     this._adapter = [jsImport.CubemapAdapter, this._config.adapter[1]];
            //     break;
            // case "cubemap-tiles": // TODO
            //     this._adapter = [jsImport.CubemapTilesAdapter, this._config.adapter[1]];
            //     break;
            // case "cubemap-video": // TODO
            //     this._adapter = [jsImport.CubemapVideoAdapter, this._config.adapter[1]];
            //     break;
        }
        return [adapter, plugins];
    }
    _addTilesDetails() {
        if (!this._config)
            throw new Error("Config not loaded. Can't init viewer.");
        this._nodes?.forEach(node => {
            const panorama = JSON.parse(node.panorama);
            panorama.tileUrl = (col, row) => `${this._baseUrl}assets/panoramas/${node.id}/tile${col + (row * panorama.cols)}.jpg`;
            panorama.baseUrl = `${this._baseUrl}assets/panoramas/${node.id}/base.jpg`;
            node.panorama = panorama;
        });
    }
    _addVideoPanoramaDetails() {
        if (!this._config)
            throw new Error("Config not loaded. Can't init viewer.");
        this._nodes?.forEach(node => {
            node.panorama = JSON.parse(node.panorama);
        });
    }
    async _initViewer(introAnimation = false) {
        if (!this._config)
            throw new Error("Config not loaded. Can't init viewer.");
        if (!this._config.viewerConfig.container)
            throw new Error("No container specified.");
        const useCDN = this._config.useCDN ?? false;
        // load core
        await loadCSS(useCDN ?
            "https://cdn.jsdelivr.net/npm/@photo-sphere-viewer/core/index.min.css" :
            `${this._baseUrl}resources/node_modules_css/core/index.css`);
        this._viewer = new (useCDN ?
            await import(/* webpackIgnore: true */ "@photo-sphere-viewer/core") :
            await import("@photo-sphere-viewer/core"))
            .Viewer(this._config.viewerConfig);
        await new Promise(resolve => this._viewer.addEventListener("ready", async () => {
            if (this._config?.introAnimation && introAnimation) {
                intro(this._viewer, this._initialViewerConfig.defaultYaw, this._initialViewerConfig.defaultPitch)
                    .then(() => {
                    // restore initial config values modified by intro animation preparations
                    this._config.viewerConfig.defaultZoomLvl = this._initialViewerConfig.defaultZoomLvl;
                    this._config.viewerConfig.fisheye = this._initialViewerConfig.fisheye;
                    this._config.viewerConfig.maxFov = this._initialViewerConfig.maxFov;
                    this._config.viewerConfig.defaultYaw = this._initialViewerConfig.defaultYaw;
                    this._config.viewerConfig.defaultPitch = this._initialViewerConfig.defaultPitch;
                });
            }
            const markers = this._viewer.getPlugin("markers");
            const virtualTour = this._viewer.getPlugin("virtual-tour");
            const plan = this._viewer.getPlugin("plan");
            if (markers) {
                markerListeners.attachListeners(this._viewer, markers, () => {
                    try {
                        return virtualTour?.getCurrentNode().caption ?? "";
                    }
                    catch (e) {
                        return "";
                    }
                });
            }
            if (virtualTour) {
                virtualTourListeners.attachListeners(this._viewer, virtualTour, this);
            }
            if (virtualTour && plan) {
                planListeners.attachListeners(this._config, virtualTour, plan, this);
            }
            resolve();
        }));
    }
    switchTour(loadTour) {
        const lastPostion = this._viewer?.getPosition();
        if (!lastPostion)
            return;
        this._viewer?.destroy();
        this._configUrl = loadTour.configUrl;
        this.init({
            startNodeId: loadTour.startNodeId,
            defaultYaw: lastPostion.yaw,
            defaultPitch: lastPostion.pitch,
        });
    }
    ;
}
